<script lang="ts" setup>
    import { computed, reactive } from 'vue'
    import { useI18n } from 'vue-i18n'
    import type { UIChatMessage } from 'models'
    import { stringifyPayload, WEB_SEARCH_TOOL_TYPES } from './utils'

    type MessagePart = UIChatMessage['parts'][number]

    interface DetailHit {
        key: string
        icon: string
        title: string
        url?: string
        meta?: string
        relevance?: number
        body?: string
    }

    interface Detail {
        chips?: string[]
        fact?: { icon: string; text: string }
        hits?: DetailHit[]
        showEmpty?: boolean
        passage?: { title?: string; meta?: string; content: string }
        rawInput?: string
        rawOutput?: string
    }

    const props = defineProps<{
        part: MessagePart
    }>()

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    const asRecord = (value: unknown): Record<string, unknown> | undefined =>
        value && typeof value === 'object' && !Array.isArray(value)
            ? (value as Record<string, unknown>)
            : undefined

    const pickString = (
        record: Record<string, unknown> | undefined,
        ...keys: string[]
    ): string | undefined => {
        for (const key of keys) {
            const value = record?.[key]
            if (typeof value === 'string' && value.trim()) {
                return value
            }
        }
        return undefined
    }

    const formatCoordinates = (
        record?: Record<string, unknown>,
    ): string | undefined => {
        const latitude = record?.latitude
        const longitude = record?.longitude
        if (typeof latitude !== 'number' || typeof longitude !== 'number') {
            return undefined
        }
        return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
    }

    const rawInputValue = computed(() =>
        'input' in props.part ? props.part.input : undefined,
    )
    const rawOutputValue = computed(() =>
        'output' in props.part ? props.part.output : undefined,
    )
    const input = computed(() => asRecord(rawInputValue.value))
    const output = computed(() => asRecord(rawOutputValue.value))

    // Search queries shown as chips (queries[], query or objective)
    const queryChips = computed<string[]>(() => {
        const raw =
            input.value?.queries ?? input.value?.query ?? input.value?.objective
        if (typeof raw === 'string' && raw.trim()) {
            return [raw]
        }
        if (Array.isArray(raw)) {
            return raw.filter(
                (query): query is string =>
                    typeof query === 'string' && Boolean(query.trim()),
            )
        }
        return []
    })

    // Raw scores live on different scales depending on the agent search mode
    // (RRF ~0.03, cosine 0 to 1) and tend to cluster, so a plain ratio on the
    // max renders every bar as ~100%. Spread them on a min/max scale with a
    // floor so the ordering stays visible, and hide the bar entirely when it
    // carries no information (single hit or identical scores).
    const spreadRelevance = (scores: number[]): (number | undefined)[] => {
        const min = Math.min(...scores)
        const max = Math.max(...scores)
        if (scores.length < 2 || max <= min) {
            return scores.map(() => undefined)
        }
        const floor = 0.15
        return scores.map(
            (score) => floor + ((score - min) / (max - min)) * (1 - floor),
        )
    }

    // Normalizes a raw result array into records paired with a spread
    // relevance derived from each record's `similarity` score.
    const scoredRecords = (
        raw: unknown,
    ):
        | { record: Record<string, unknown>; relevance?: number }[]
        | undefined => {
        if (!Array.isArray(raw)) {
            return undefined
        }
        const records = raw
            .map((item) => asRecord(item))
            .filter((record): record is Record<string, unknown> =>
                Boolean(record),
            )
        const relevances = spreadRelevance(
            records.map((record) =>
                typeof record.similarity === 'number' ? record.similarity : 0,
            ),
        )
        return records.map((record, index) => ({
            record,
            relevance: relevances[index],
        }))
    }

    // searchInKnowledgeBase: group chunks by document, relevance compares the
    // best chunk score of each document within the result set.
    const knowledgeHits = computed<DetailHit[] | undefined>(() => {
        const results = output.value?.results
        if (!Array.isArray(results)) {
            return undefined
        }
        interface DocumentGroup {
            id: string
            type: string
            title: string
            url?: string
            similarity: number
            chunks: string[]
        }
        const groups = new Map<string, DocumentGroup>()
        for (const [index, raw] of results.entries()) {
            const result = asRecord(raw)
            if (!result) {
                continue
            }
            const document = asRecord(result.document)
            const source = asRecord(document?.source)
            const id =
                typeof document?.id === 'string' ? document.id : `doc-${index}`
            const similarity =
                typeof result.similarity === 'number' ? result.similarity : 0
            let group = groups.get(id)
            if (!group) {
                group = {
                    id,
                    type:
                        typeof document?.type === 'string' ? document.type : '',
                    title:
                        pickString(document, 'name') ??
                        pickString(source, 'filename', 'url') ??
                        $t('messagePart.stepDocument'),
                    url: pickString(source, 'url'),
                    similarity,
                    chunks: [],
                }
                groups.set(id, group)
            }
            group.similarity = Math.max(group.similarity, similarity)
            if (typeof result.content === 'string' && result.content.trim()) {
                group.chunks.push(result.content)
            }
        }
        const list = [...groups.values()]
        const relevances = spreadRelevance(list.map((g) => g.similarity))
        return list.map((group, index) => ({
            key: group.id,
            icon: group.type === 'url' ? 'ri:global-line' : 'ri:file-text-line',
            title: group.title,
            url: group.url,
            meta: $t('messagePart.stepExcerpts', group.chunks.length || 1),
            relevance: relevances[index],
            body: group.chunks.join('\n\n· · ·\n\n') || undefined,
        }))
    })

    // retrieveRevisedAnswers: one hit per matched question, the corrected
    // answer is the expandable body.
    const revisedAnswerHits = computed<DetailHit[] | undefined>(() =>
        scoredRecords(output.value?.results)?.map(
            ({ record, relevance }, index) => ({
                key:
                    pickString(record, 'revisedAnswerId') ?? `revised-${index}`,
                icon: 'ri:file-edit-line',
                title:
                    pickString(record, 'userQuestion') ??
                    $t('messagePart.revisedAnswers'),
                relevance,
                body: pickString(record, 'revisedMessage'),
            }),
        ),
    )

    // recallUserMemory / saveUserMemory delete candidates: one hit per
    // memory, long facts expand to their full text.
    const memoryHits = (raw: unknown): DetailHit[] | undefined =>
        scoredRecords(raw)?.map(({ record, relevance }, index) => {
            const content = pickString(record, 'content') ?? ''
            return {
                key: pickString(record, 'id') ?? `memory-${index}`,
                icon: 'ri:brain-line',
                title: content,
                meta: pickString(record, 'updatedAt')?.slice(0, 10),
                relevance,
                body: content.length > 120 ? content : undefined,
            }
        })

    // Query chips over a list of hits, flagging an explicit empty state so the
    // "no results" message shows once the tool has run (hits defined, length 0).
    const hitsDetail = (hits: DetailHit[] | undefined): Detail => ({
        chips: queryChips.value,
        hits,
        showEmpty: hits !== undefined && !hits.length,
    })

    // Rich, tool-specific view of the step. Returns {} when the tool has
    // nothing meaningful to show yet (the raw fallback is applied by `detail`).
    const buildDetail = (): Detail => {
        const type = props.part.type
        if (type === 'tool-searchInKnowledgeBase') {
            return hitsDetail(knowledgeHits.value)
        }
        if (type === 'tool-retrieveRevisedAnswers') {
            return hitsDetail(revisedAnswerHits.value)
        }
        if (type === 'tool-expandChunkContext') {
            const content = pickString(output.value, 'content')
            if (!content) {
                return {}
            }
            const totalChunks = output.value?.totalChunksReturned
            return {
                passage: {
                    title: pickString(output.value, 'documentName'),
                    meta:
                        typeof totalChunks === 'number'
                            ? $t('messagePart.stepExcerpts', totalChunks)
                            : undefined,
                    content,
                },
            }
        }
        if (type === 'tool-readDocumentFile') {
            const name = pickString(output.value, 'name')
            if (!name) {
                return {}
            }
            const mimeType = pickString(output.value, 'mimeType')
            return {
                fact: {
                    icon: 'ri:file-text-line',
                    text: mimeType ? `${name} (${mimeType})` : name,
                },
            }
        }
        if (type === 'tool-getWeather') {
            const location =
                pickString(output.value, 'location') ??
                pickString(input.value, 'locationName') ??
                formatCoordinates(input.value)
            if (!location) {
                return {}
            }
            const startDate = pickString(input.value, 'startDate')
            const endDate = pickString(input.value, 'endDate')
            const dateRange = startDate
                ? endDate && endDate !== startDate
                    ? ` · ${startDate} → ${endDate}`
                    : ` · ${startDate}`
                : ''
            return {
                fact: {
                    icon: 'ri:sun-cloudy-line',
                    text: `${location}${dateRange}`,
                },
            }
        }
        if (type === 'tool-geocode') {
            const from =
                pickString(input.value, 'locationName') ??
                formatCoordinates(input.value)
            const to =
                pickString(
                    output.value,
                    'name',
                    'displayName',
                    'locationName',
                    'label',
                ) ?? formatCoordinates(output.value)
            const text =
                from && to && from !== to ? `${from} → ${to}` : (from ?? to)
            if (!text) {
                return {}
            }
            return {
                fact: {
                    icon: 'ri:map-pin-line',
                    text,
                },
            }
        }
        if (type === 'tool-recallUserMemory') {
            return hitsDetail(memoryHits(output.value?.memories))
        }
        if (type === 'tool-saveUserMemory') {
            const candidates = memoryHits(output.value?.candidates)
            if (candidates) {
                return hitsDetail(candidates)
            }
            const text =
                pickString(input.value, 'content') ??
                pickString(output.value, 'message', 'error')
            if (!text) {
                return {}
            }
            return {
                fact: {
                    icon:
                        pickString(input.value, 'action') === 'delete'
                            ? 'ri:delete-bin-line'
                            : 'ri:save-3-line',
                    text,
                },
            }
        }
        if (WEB_SEARCH_TOOL_TYPES.includes(type)) {
            return {
                chips: queryChips.value,
                rawOutput: stringifyPayload(rawOutputValue.value),
            }
        }
        return {}
    }

    const detail = computed<Detail>(() => {
        const result = buildDetail()
        // Nothing meaningful to show for this tool yet (e.g. still running):
        // fall back to the raw payloads so the step body is never empty.
        const hasRichContent =
            result.chips?.length ||
            result.fact ||
            result.passage ||
            result.showEmpty ||
            result.hits?.length
        if (!hasRichContent) {
            result.rawInput = stringifyPayload(rawInputValue.value)
            result.rawOutput ??= stringifyPayload(rawOutputValue.value)
        }
        return result
    })

    const expandedHits = reactive(new Set<string>())
    const toggleHit = (key: string) => {
        if (expandedHits.has(key)) {
            expandedHits.delete(key)
        } else {
            expandedHits.add(key)
        }
    }
</script>

<template>
    <div class="pk-chatbot-step-detail">
        <ul v-if="detail.chips?.length" class="pk-chatbot-step-detail__chips">
            <li
                v-for="(chip, chipIndex) in detail.chips"
                :key="`${chipIndex}-${chip}`"
                class="pk-chatbot-step-detail__chip">
                <VvIcon name="ri:search-line" class="shrink-0" />
                <span class="pk-chatbot-step-detail__chip-text">
                    {{ chip }}
                </span>
            </li>
        </ul>
        <div v-if="detail.fact" class="pk-chatbot-step-detail__fact">
            <VvIcon :name="detail.fact.icon" class="shrink-0" />
            <span>{{ detail.fact.text }}</span>
        </div>
        <div v-if="detail.showEmpty" class="pk-chatbot-step-detail__empty">
            {{ $t('messagePart.stepNoResults') }}
        </div>
        <ul
            v-else-if="detail.hits?.length"
            class="pk-chatbot-step-detail__hits">
            <li v-for="hit in detail.hits" :key="hit.key">
                <component
                    :is="hit.body ? 'button' : 'div'"
                    :type="hit.body ? 'button' : undefined"
                    class="pk-chatbot-step-detail__hit-header"
                    :aria-expanded="
                        hit.body ? expandedHits.has(hit.key) : undefined
                    "
                    @click="hit.body ? toggleHit(hit.key) : undefined">
                    <VvIcon
                        :name="hit.icon"
                        class="pk-chatbot-step-detail__hit-icon" />
                    <span class="pk-chatbot-step-detail__hit-heading">
                        <span class="pk-chatbot-step-detail__hit-title">
                            {{ hit.title }}
                        </span>
                        <a
                            v-if="hit.url"
                            :href="hit.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="pk-chatbot-step-detail__link"
                            @click.stop>
                            <VvIcon name="ri:external-link-line" />
                        </a>
                    </span>
                    <span
                        v-if="hit.meta"
                        class="pk-chatbot-step-detail__hit-meta">
                        {{ hit.meta }}
                    </span>
                    <span
                        v-if="hit.relevance !== undefined"
                        class="pk-chatbot-step-detail__relevance"
                        aria-hidden="true">
                        <span
                            class="pk-chatbot-step-detail__relevance-fill"
                            :style="{
                                width: `${Math.round(hit.relevance * 100)}%`,
                            }" />
                    </span>
                    <VvIcon
                        v-if="hit.body"
                        :name="
                            expandedHits.has(hit.key)
                                ? 'ri:arrow-up-s-line'
                                : 'ri:arrow-down-s-line'
                        "
                        class="shrink-0" />
                </component>
                <div
                    v-if="hit.body && expandedHits.has(hit.key)"
                    class="pk-chatbot-step-detail__hit-body">
                    {{ hit.body }}
                </div>
            </li>
        </ul>
        <div v-if="detail.passage" class="pk-chatbot-step-detail__passage">
            <div class="pk-chatbot-step-detail__passage-header">
                <VvIcon name="ri:file-text-line" class="shrink-0" />
                <span class="pk-chatbot-step-detail__hit-heading">
                    <span class="pk-chatbot-step-detail__hit-title">
                        {{
                            detail.passage.title ??
                            $t('messagePart.stepDocument')
                        }}
                    </span>
                </span>
                <span
                    v-if="detail.passage.meta"
                    class="pk-chatbot-step-detail__hit-meta">
                    {{ detail.passage.meta }}
                </span>
            </div>
            <div class="pk-chatbot-step-detail__passage-content">
                {{ detail.passage.content }}
            </div>
        </div>
        <div v-if="detail.rawInput" class="pk-chatbot-steps__step-payload">
            <span class="pk-chatbot-steps__step-payload-label">
                {{ $t('messagePart.stepInput') }}
            </span>
            <pre>{{ detail.rawInput }}</pre>
        </div>
        <div v-if="detail.rawOutput" class="pk-chatbot-steps__step-payload">
            <span class="pk-chatbot-steps__step-payload-label">
                {{ $t('messagePart.stepOutput') }}
            </span>
            <pre>{{ detail.rawOutput }}</pre>
        </div>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-step-detail {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-8);
        font-size: var(--text-12);
        color: var(--color-word-3);

        &__chips {
            display: flex;
            flex-wrap: wrap;
            gap: var(--spacing-4);
        }

        &__chip {
            display: inline-flex;
            align-items: center;
            gap: var(--spacing-4);
            max-width: 100%;
            padding: var(--spacing-2) var(--spacing-8);
            border: var(--spacing-px) solid var(--color-surface-4);
            border-radius: var(--rounded-full);
            background-color: var(--color-surface-1);
            color: var(--color-word-2);
        }

        &__chip-text {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__fact {
            display: flex;
            align-items: center;
            gap: var(--spacing-8);
            color: var(--color-word-2);
        }

        &__empty {
            color: var(--color-word-4);
            font-style: italic;
        }

        &__hits {
            display: flex;
            flex-direction: column;
            border: var(--spacing-px) solid var(--color-surface-4);
            border-radius: var(--rounded);
            background-color: var(--color-surface-1);
            overflow: hidden;

            > li + li {
                border-top: var(--spacing-px) solid var(--color-surface-3);
            }
        }

        &__hit-header {
            display: flex;
            align-items: center;
            gap: var(--spacing-8);
            width: 100%;
            min-height: var(--spacing-32);
            padding: var(--spacing-4) var(--spacing-8);
            text-align: left;

            &:is(button) {
                cursor: pointer;
                transition: background-color 0.2s ease;

                &:hover {
                    background-color: var(--color-surface-2);
                }
            }
        }

        &__hit-icon {
            flex-shrink: 0;
            color: var(--color-word-3);
        }

        // Grows to fill the row so meta, relevance and chevron stay aligned;
        // the title inside only shrinks, keeping the link icon next to the
        // title text instead of pushed to the right edge.
        &__hit-heading {
            display: flex;
            align-items: center;
            gap: var(--spacing-4);
            flex: 1 1 0;
            min-width: 0;
        }

        &__hit-title {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--color-word-2);
        }

        &__hit-meta {
            color: var(--color-word-4);
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
        }

        &__relevance {
            flex-shrink: 0;
            width: var(--spacing-40);
            height: var(--spacing-4);
            border-radius: var(--rounded-full);
            background-color: var(--color-surface-3);
            overflow: hidden;
        }

        &__relevance-fill {
            display: block;
            height: 100%;
            border-radius: inherit;
            background-color: var(--chatbot-main-color, var(--color-word-4));
        }

        &__link {
            flex-shrink: 0;
            display: inline-flex;
            color: var(--color-word-4);

            &:hover {
                color: var(--color-word-1);
            }
        }

        &__hit-body,
        &__passage-content {
            padding: var(--spacing-8);
            max-height: var(--spacing-160);
            overflow: auto;
            white-space: pre-wrap;
            word-break: break-word;
            color: var(--color-word-3);
        }

        &__hit-body {
            border-top: var(--spacing-px) solid var(--color-surface-3);
        }

        &__passage {
            border: var(--spacing-px) solid var(--color-surface-4);
            border-radius: var(--rounded);
            background-color: var(--color-surface-1);
            overflow: hidden;
        }

        &__passage-header {
            display: flex;
            align-items: center;
            gap: var(--spacing-8);
            min-height: var(--spacing-32);
            padding: var(--spacing-4) var(--spacing-8);
            border-bottom: var(--spacing-px) solid var(--color-surface-3);
        }
    }
</style>
